import { useState } from "react";
import { apiPost, ApiResponse, ApiError } from "../../services/api.service";
import { showSuccess } from "../../services/notification.service";

interface ApplyResult {
	success: boolean;
	applied: number[];
	alreadyApplied: number[];
	errors: Array<{ eventId: number; reason: string }>;
}

interface ApplyToEventsParams {
	extra_id: number;
	event_ids: number[];
}

interface UseApplyToEventsReturn {
	applyToEvents: (params: ApplyToEventsParams) => Promise<ApplyResult | null>;
	loading: boolean;
	error: string | null;
	data: ApplyResult | null;
}

/**
 * Custom hook to apply to multiple events
 * @returns Object containing applyToEvents function, loading state, error state, and response data
 */
export function useApplyToEvents(): UseApplyToEventsReturn {
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<ApplyResult | null>(null);

	const applyToEvents = async (
		params: ApplyToEventsParams
	): Promise<ApplyResult | null> => {
		try {
			setLoading(true);
			setError(null);

			const response: ApiResponse<ApplyResult> = await apiPost<ApplyResult>(
				"/events/apply",
				params,
				{
					notification: { enabled: false },
				}
			);

			if (response.success && response.data) {
				setData(response.data);
				// Show custom success notification
				if (response.message) {
					showSuccess(
						`${response.message} - Wir werden uns in ein paar Tagen bei Ihnen bezüglich der nächsten Schritte melden.`
					);
				}
				return response.data;
			} else {
				// Use the backend's error message directly
				const errorMessage =
					response.message ||
					response.error ||
					"Fehler bei der Terminauswahl";
				setError(errorMessage);
				// Error notification is handled by API service
				return null;
			}
		} catch (err) {
			let errorMessage =
				"Ein unerwarteter Fehler ist beim Auswählen von Terminen aufgetreten";

			if (err instanceof ApiError) {
				// Check if error has data with message property
				if (err.data && err.data.message) {
					errorMessage = err.data.message;
				} else {
					errorMessage = err.message;
				}
			} else if (err instanceof Error) {
				errorMessage = err.message;
			}

			setError(errorMessage);
			// Error notification is handled by API service
			return null;
		} finally {
			setLoading(false);
		}
	};

	return {
		applyToEvents,
		loading,
		error,
		data,
	};
}
